import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import useApi from "../../../shared/hooks/useApi";
import { VisitInstanceId } from "../../../shared/schema/schema";

export const MAX_RESUBMIT_VISITS = 100;

export type ResubmitAggregator = "sandata" | "hhax";

/** The re-submit endpoint a contract's EVV aggregator name maps to, if it has one. */
export function getResubmitAggregatorFor(
  evvAggregator: string | null | undefined
): ResubmitAggregator | null {
  const aggregator = evvAggregator?.toLowerCase();
  if (aggregator === "sandata") return "sandata";
  if (aggregator === "hhaexchange - sftp" || aggregator === "hhaexchange - api") return "hhax";
  return null;
}

export type ResubmitGroup = { aggregator: ResubmitAggregator; visitInstanceIds: VisitInstanceId[] };

/**
 * Re-submits visits, dispatching per aggregator since Sandata and HHAX have separate endpoints.
 * Sandata's endpoint sync-submits and returns "submitted"; HHAX's endpoint clears state and the
 * next daily worker run picks the visit up — HHAX returns "queued". Both count as success.
 *
 * The summary / error toasts live here so every page shows the same ones; the caller supplies
 * what else to do afterwards (clear a selection, close a dialog, refetch).
 */
export function useResubmitEvvVisits(callbacks: { onSuccess: () => void; onError: () => void }) {
  const { api } = useApi();
  const toast = useToast();

  return useMutation({
    mutationFn: async (groups: ResubmitGroup[]) => {
      const sandataIds = groups.find((g) => g.aggregator === "sandata")?.visitInstanceIds ?? [];
      const hhaxIds = groups.find((g) => g.aggregator === "hhax")?.visitInstanceIds ?? [];

      const [sandataResp, hhaxResp] = await Promise.all([
        sandataIds.length > 0
          ? api.post("./sandata_visits/resubmit", { body: { visitInstanceIds: sandataIds } })
          : Promise.resolve({ results: [] }),
        hhaxIds.length > 0
          ? // HHAX endpoint: not yet in generated schema, so call via untyped path.
            (api as any).post("./hhax_visits/resubmit", {
              body: { visitInstanceIds: hhaxIds },
            })
          : Promise.resolve({ results: [] }),
      ]);

      return { sandata: sandataResp, hhax: hhaxResp };
    },
    onSuccess: (data: any) => {
      const sandataResults: any[] = data?.sandata?.results ?? [];
      const hhaxResults: any[] = data?.hhax?.results ?? [];

      // Both Sandata "submitted" and HHAX "queued" represent success from the operator's view.
      const succeeded =
        sandataResults.filter((r) => r.status === "submitted").length +
        hhaxResults.filter((r) => r.status === "queued").length;
      const failed = [...sandataResults, ...hhaxResults].filter(
        (r) => r.status === "failed"
      ).length;
      const rateLimited = [...sandataResults, ...hhaxResults].filter(
        (r) => r.status === "rate_limited"
      ).length;

      let description = `${succeeded} visit(s) re-submitted successfully.`;
      if (failed > 0) description += ` ${failed} failed.`;
      if (rateLimited > 0) description += ` ${rateLimited} exceeded daily limit.`;
      description += " Results may take a few minutes — refresh the page to check final status.";

      toast({
        title: "Re-submission complete",
        description,
        status: failed > 0 || rateLimited > 0 ? "warning" : "success",
        duration: 10000,
        isClosable: true,
      });

      callbacks.onSuccess();
    },
    onError: (error: Error) => {
      toast({
        title: "Re-submission failed",
        description: error.message,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
      callbacks.onError();
    },
  });
}
